import { buttonAnimationCss } from './theme.js';
/**
 * The offer cards' styles, ported rule-by-rule from `.cod-offer*` in
 * `form-renderer/src/styles.ts:220-345`.
 *
 * The legacy drove these from four CSS custom properties on the container
 * (`--cod-offer-bg/-border/-radius/-price-size`) so the template CSS could stay static. There is
 * no cascade here — every object is self-contained and the design values are baked in, which is
 * the same rule the rest of `theme.ts` follows.
 */
const px = (n) => `${n}px`;
/**
 * The card's own CSS, through `styled()` rather than the style prop.
 *
 * A card is a `button`, which is the most heavily styled selector on any storefront theme — and an
 * `!important` in a style-prop value does not beat an `!important` author rule (`MIGRATION.md`
 * §4.11). On a live store the theme uppercased the tier titles and forced its own padding.
 */
export function offerCardCss(textColor) {
    return ('&{text-transform:none !important;letter-spacing:normal !important;' +
        `text-decoration:none !important;color:${textColor} !important;` +
        'padding:0 !important;min-height:0 !important;height:auto !important;' +
        'line-height:1.4 !important;text-align:left !important}');
}
/**
 * The "Best deal" badge's CSS, through `styled()` — `''` when the merchant chose no animation.
 *
 * 🚨 `@keyframes` is only expressible in this channel; a style object cannot carry one
 * (`MIGRATION.md` §4.11). The names come from `buttonAnimationCss`, so the badge animates with the
 * exact same four keyframe sets as the COD and action buttons — one implementation, and the legacy
 * bundle's `.cod-anim-*` classes are those same frames.
 *
 * Callers apply it ONLY when there is something to emit, so an un-animated badge stays a plain
 * `text` with no `styled()` wrapper at all (see `bestDealNode`).
 */
export function bestDealCss(design) {
    return buttonAnimationCss(design.best_deal_animation);
}
/** Same reset as `theme.ts`: no shadow root, so a theme's typography reaches these too. */
const RESET = {
    textTransform: 'none !important',
    letterSpacing: 'normal !important',
    textDecoration: 'none !important',
    lineHeight: '1.4',
    margin: '0',
    // Inherited from the panel for offers inside the form, but the cards above the buy button are
    // rendered into a theme slot instead — see the containers below. Restated per caption so a tier
    // title breaks in BOTH places. Captions that must not break carry `white-space: nowrap`, which
    // wins outright.
    overflowWrap: 'anywhere',
};
/**
 * 🚨 `textColor` is REQUIRED, not optional.
 *
 * Every offer card is a `button` (only `button` and `link` take an `onClick`), so anything inside
 * it that leaves `color` unset inherits the **theme's button text colour** — which is white on the
 * dark buttons most themes ship. On a live store that rendered the tier titles in the theme's link
 * blue and made the variant dropdowns look EMPTY: the closed `<select>` painted white-on-white,
 * while opening it showed the options fine because the native popup uses OS colours.
 */
export function offerStyles(design, textColor) {
    const radius = px(design.border_radius);
    const accent = design.border_color;
    // `.cod-offer` — the unselected card. Selection swaps the border for a 2px accent one and the
    // background for the design's, so the two objects must agree on everything else or the card
    // shifts by a pixel when picked.
    const cardBase = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: radius,
        overflow: 'hidden',
        padding: '0',
        textAlign: 'left',
        cursor: 'pointer',
    };
    const radioBase = {
        width: '18px',
        height: '18px',
        borderRadius: '999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxSizing: 'border-box',
    };
    const plaqueBase = {
        ...RESET,
        fontSize: '12px',
        fontWeight: '700',
        padding: '3px 10px',
        borderRadius: '999px',
        whiteSpace: 'nowrap',
    };
    return {
        // `.cod-offers { display:flex; flex-direction:column; gap:12px; margin-bottom:18px }`
        container: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginBottom: '18px',
            width: '100%',
            boxSizing: 'border-box',
            overflowWrap: 'anywhere',
        },
        // 🚨 `overflowWrap` here is not a duplicate of the panel's.
        //
        // These cards are rendered into a PRODUCT-PAGE slot, not into the form, so they inherit the
        // theme's wrapping rather than ours. Without it a tier title with one long word sets the
        // min-content width of the theme's own buy-button column and widens the product page.
        containerAbove: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            marginTop: '18px',
            marginBottom: '18px',
            width: '100%',
            boxSizing: 'border-box',
            overflowWrap: 'anywhere',
        },
        // `.cod-offer-vertical-row { display:flex; flex-wrap:nowrap; align-items:stretch; gap:12px }`
        verticalRow: {
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            gap: '12px',
            width: '100%',
        },
        card: { ...cardBase, border: '1px solid rgba(0,0,0,.14)', backgroundColor: '#ffffff' },
        // `.cod-offer-selected { border: 2px solid var(--cod-offer-border); background: var(--cod-offer-bg) }`
        cardSelected: { ...cardBase, border: `2px solid ${accent}`, backgroundColor: design.bg_color },
        // `.cod-offer-vertical { flex: 1 1 0; min-width: 0 }`
        cardVertical: { flexGrow: '1', flexShrink: '1', flexBasis: '0%', minWidth: '0' },
        // Chrome-free: the frame (border, background, radius) belongs to the card around it, so the
        // button contributes nothing visual and only makes its own row clickable.
        cardButton: {
            display: 'block',
            width: '100%',
            padding: '0',
            margin: '0',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            color: 'inherit',
        },
        // Vertical only: the button is the card's single child, so it has to grow for the layout inside
        // it to have any height to distribute. A flex column rather than a block, so `mainVertical`'s
        // own `flexGrow` has an axis to grow along without depending on a percentage-height chain.
        cardButtonVertical: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: '1',
            width: '100%',
            padding: '0',
            margin: '0',
            border: 'none',
            backgroundColor: 'transparent',
            textAlign: 'left',
            cursor: 'pointer',
            color: 'inherit',
        },
        // `.cod-offer-main { display:flex; align-items:center; gap:14px; padding:12px 14px }`
        main: {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            padding: '12px 14px',
            width: '100%',
            boxSizing: 'border-box',
        },
        // Modern: `padding: 0; gap: 0; align-items: stretch` — the plaque is a full-height block.
        mainModern: {
            display: 'flex',
            alignItems: 'stretch',
            gap: '0',
            padding: '0',
            width: '100%',
            boxSizing: 'border-box',
        },
        // Vertical: a column, `padding: 0` — the plaque is a full-width footer.
        //
        // 🚨 `flexGrow` (with `cardButtonVertical`) is what pins that footer to the BOTTOM. The cards
        // share a row and stretch to the tallest one, but this column only ever grew to its own
        // content, so a shorter card's plaque stopped mid-card — three cards, three plaque heights.
        // Growing it hands the slack to `bodyVertical`, which already grows, and the plaque lands last.
        mainVertical: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            flexGrow: '1',
            gap: '0',
            padding: '0',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
        },
        // `.cod-offer-modern .cod-offer-body { padding: 16px 44px 16px 18px }` — the right padding is
        // the room the absolutely-positioned radio occupies.
        bodyModern: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexGrow: '1',
            minWidth: '0',
            padding: '16px 44px 16px 18px',
            boxSizing: 'border-box',
        },
        // `.cod-offer-vertical .cod-offer-body { align-items:center; text-align:center; gap:8px;
        //  padding:16px 14px }`
        bodyVertical: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: '8px',
            flexGrow: '1',
            padding: '16px 14px',
            boxSizing: 'border-box',
        },
        info: { flexGrow: '1', minWidth: '0' },
        // `.cod-offer-tail { align-self:stretch; align-items:flex-end; justify-content:space-between }`
        //
        // 🚨 The tail SHRINKS. It used to be `flex: 0 0 auto`, which reserved the full max-content width
        // of the badges row — a plaque plus a "Best deal" pill side by side — and left the info column
        // too narrow for its own prices, which then overflowed to the right and were painted over by
        // those pills. Shrinking lets the badges row wrap (it already may) so the pills stack instead.
        // No `min-width: 0` here on purpose: the default `auto` floors the tail at one pill's width,
        // which is where it should stop.
        tail: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            alignSelf: 'stretch',
            flexShrink: 1,
            gap: '8px',
        },
        title: { ...RESET, color: textColor, fontSize: '15px', fontWeight: '700' },
        // `.cod-offer-prices { display:flex; align-items:baseline; gap:8px; margin-top:3px }`
        // Wraps: when the card is narrow the struck-through base drops above the net price instead of
        // the pair overflowing the info column. Each amount is `nowrap` so a wrap never falls between
        // the currency and the number ("COP" / "75.000").
        prices: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'baseline',
            gap: '8px',
            marginTop: '3px',
        },
        // Modern and vertical stack them right-/centre-aligned with no top margin.
        pricesStacked: { display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '0' },
        // `.cod-offer-vertical .cod-offer-prices { margin-top: auto }` — vertical's body grows to the
        // card's full height, so the prices are pushed to its bottom and line up across the cards
        // however much the titles differ, rather than floating wherever the title happens to end.
        pricesVertical: { display: 'flex', flexDirection: 'column', gap: '2px', marginTop: 'auto' },
        compare: {
            ...RESET,
            fontSize: '13px',
            color: 'rgba(0,0,0,.5)',
            textDecoration: 'line-through !important',
            whiteSpace: 'nowrap',
        },
        // `fontSize` and `color` are overwritten per tier by the caller (`prices_size`, `price_color`).
        price: { ...RESET, fontWeight: '600', lineHeight: '1.2', whiteSpace: 'nowrap' },
        plaque: plaqueBase,
        // `.cod-offer-modern .cod-offer-plaque { width:92px; border-radius:0; font-size:14px;
        //  white-space:normal }` — a full-height block on the card's left edge.
        plaqueModern: {
            ...plaqueBase,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '92px',
            padding: '0 8px',
            borderRadius: '0',
            fontSize: '14px',
            textAlign: 'center',
            whiteSpace: 'normal',
        },
        // `.cod-offer-badges` — the plaque and the "Best deal" badge on one line. Wraps rather than
        // overflows: two pills plus a narrow card (the sidebar above the buy button) do not always fit,
        // and a card that grows a row is better than a caption clipped by the card's `overflow:hidden`.
        badges: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: '6px',
        },
        // Same pill metrics as the plaque, but the radius is the merchant's (the plaque's is fixed).
        // `backgroundColor`/`color`/`borderRadius` are overwritten per offer by the caller.
        bestDeal: plaqueBase,
        // Modern's body and vertical's column are both narrower than the classic tail's wrapping row,
        // and the card clips its overflow — so here the caption wraps instead of being cut off.
        bestDealStacked: {
            ...plaqueBase,
            alignSelf: 'center',
            textAlign: 'center',
            whiteSpace: 'normal',
        },
        // `.cod-offer-vertical .cod-offer-plaque { border-radius:0; padding:10px 8px }` — a footer.
        plaqueVertical: {
            ...plaqueBase,
            borderRadius: '0',
            padding: '10px 8px',
            textAlign: 'center',
            whiteSpace: 'normal',
            width: '100%',
            boxSizing: 'border-box',
        },
        radio: { ...radioBase, border: '2px solid rgba(0,0,0,.3)' },
        radioOn: { ...radioBase, border: `2px solid ${accent}` },
        radioDot: { width: '9px', height: '9px', borderRadius: '999px', backgroundColor: accent },
        radioModern: { position: 'absolute', top: '14px', right: '16px' },
        // `.cod-offer-thumb { width:56px; height:56px; border:1px solid rgba(0,0,0,.12) }`
        thumb: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '56px',
            height: '56px',
            border: '1px solid rgba(0,0,0,.12)',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
        },
        // `object-fit: contain`, unlike the product card's `cover` — an offer thumb must show the
        // whole product, not a crop of it.
        thumbImg: { width: '100%', height: '100%', objectFit: 'contain' },
        thumbFallback: { ...RESET, fontSize: '10px', color: '#555555', textAlign: 'center' },
        // Explicit, for the same reason as `title` — it sits inside the card button.
        // `.cod-offer-variants { display:flex; flex-direction:column; gap:8px; padding:0 14px 14px }`
        pickers: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '0 14px 14px',
            width: '100%',
            boxSizing: 'border-box',
        },
        pickersModern: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '12px 14px 14px',
            width: '100%',
            boxSizing: 'border-box',
        },
        pickerRow: { display: 'flex', alignItems: 'center', gap: '10px', width: '100%' },
        pickerNumber: {
            ...RESET,
            flexShrink: 0,
            minWidth: '16px',
            fontSize: '14px',
            fontWeight: '700',
            color: 'rgba(0,0,0,.6)',
        },
        // 🚨 The legacy's `flex: 1` belongs on a box WE own, not on the control.
        //
        // `.cod-offer-variant-select` was the flex item itself — the DOM renderer dropped the `<select>`
        // straight into the row. A `select` here is wrapped (it renders with its label), and that
        // wrapper is what the row lays out, so a `flexGrow` on the control grows nothing: the wrapper
        // shrink-wraps its content and the dropdowns huddle at the left of the row instead of filling
        // it. Same trap `fields.ts` documents for the textarea — but `select`'s style has no
        // `container` slot to fix it through (only `label` and `select`), so the row's flex item has to
        // be a box of our own. That also keeps the two paths honest: it does not depend on how the
        // host chooses to wrap.
        //
        // `flexBasis: 0` rather than the default `auto`, because `flex: 1` is `1 1 0%`: the two split
        // pickers must come out the SAME width whatever their option text says ("Negro" vs "XS"),
        // which is what the storefront shows.
        pickerField: { flexGrow: '1', flexShrink: '1', flexBasis: '0%', minWidth: '0' },
        // `.cod-offer-variant-select { flex:1; padding:11px 36px 11px 14px; font-size:14px }`. The
        // caret is the host `select`'s own — the legacy drew one with a background SVG because it had
        // set `appearance: none`.
        picker: {
            width: '100%',
            minWidth: '0',
            padding: '11px 14px',
            fontSize: '14px',
            fontWeight: '500',
            lineHeight: '1.2',
            // NOT `inherit`. The legacy inherited from `.cod-root`, a plain div; here the ancestor is a
            // button, so `inherit` picks up the theme's button colour and the closed select renders
            // invisible against its own white background.
            color: textColor,
            border: '1px solid rgba(0,0,0,.18)',
            borderRadius: radius,
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            boxSizing: 'border-box',
        },
        // 🚨 The theme's `.form-group{margin-bottom:20px}`, cancelled: the host wraps every `select`
        // in that class, so each picker row carried 20px of theme margin under it. `theme.ts`'s
        // `nativeContainer` header has the measurement.
        pickerContainer: { width: '100%', margin: '0' },
        // The pickers are numbered, not labelled — the same treatment every other input gets, since
        // the host renders a `select`'s label below it.
        pickerLabelHidden: { display: 'none' },
    };
}
//# sourceMappingURL=offers.theme.js.map